import { visit } from 'graphql';
import { GET_STOCK_POSITION } from './queries/stocks';
import { GET_TOKEN_WITH_POSITION } from './queries/tokens';

// Preserve the crypto response keys/variables for the shared UI, but never send
// a stock action to a crypto resolver. Template catalog operations stay shared.
const stockFields = {
  previewExitStrategy: 'previewStockExitStrategy',
  saveExitStrategyLevels: 'saveStockExitStrategyLevels',
  addExitLevel: 'addStockExitLevel',
  editExitLevel: 'editStockExitLevel',
  setExitLevelStatus: 'setStockExitLevelStatus',
  removeExitLevel: 'removeStockExitLevel',
  removeExitStrategy: 'removeStockExitStrategy',
  invalidationLevel: 'stockInvalidationLevel',
  applyInvalidationStrategy: 'applyStockInvalidationStrategy',
  removeInvalidationStrategy: 'removeStockInvalidationStrategy',
  updateInvalidationLevel: 'updateStockInvalidationLevel',
};
const stockDocuments = new WeakMap();

export function assetStrategyDocument(document, assetKind = 'crypto') {
  if (assetKind !== 'stock') return document;
  if (!stockDocuments.has(document)) {
    stockDocuments.set(document, visit(document, {
      OperationDefinition(node) {
        return { ...node, name: { kind: 'Name', value: `Stock${node.name?.value || 'Strategy'}` } };
      },
      Field(node, key, parent, path, ancestors) {
        // Only API root fields differ by asset. Nested Position fields keep
        // their shared schema names (notably Position.invalidationLevel).
        const operation = ancestors.find((ancestor) => ancestor?.kind === 'OperationDefinition');
        if (!operation?.selectionSet.selections.includes(node)) return undefined;
        const stockName = stockFields[node.name.value];
        if (!stockName) return undefined;
        return {
          ...node,
          alias: node.alias || node.name,
          name: { ...node.name, value: stockName },
          arguments: node.arguments.map((arg) => arg.name.value === 'tokenId'
            ? { ...arg, name: { ...arg.name, value: 'stockId' } }
            : arg),
        };
      },
    }));
  }
  return stockDocuments.get(document);
}

export function strategyPositionQuery(assetKind, token, portfolioId) {
  return assetKind === 'stock'
    ? { query: GET_STOCK_POSITION, variables: { stockId: token.id, portfolioId } }
    : { query: GET_TOKEN_WITH_POSITION, variables: { coingeckoSlug: token.coingeckoSlug, portfolioId } };
}

// Adapt only the cache envelope, not the position or its currency-scaled values.
export function readStrategyPosition(cache, assetKind, token, portfolioId) {
  const data = cache.readQuery(strategyPositionQuery(assetKind, token, portfolioId));
  return assetKind === 'stock'
    ? data && { tokenWithPosition: { position: data.stockPosition } }
    : data;
}

export function writeStrategyPosition(cache, assetKind, token, portfolioId, data) {
  cache.writeQuery({
    ...strategyPositionQuery(assetKind, token, portfolioId),
    data: assetKind === 'stock' ? { stockPosition: data.tokenWithPosition.position } : data,
  });
}
